'use client';

import { useEffect, useState } from 'react';
import { getCookiePreference, saveCookiePreference, setupAutoCookieRefresh, type CookiePreference } from '@/lib/cookieConsent';
import { Cookie, MapPin } from 'lucide-react';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const preference = getCookiePreference();
    if (!preference) {
      // Show banner after a short delay for better UX
      setTimeout(() => {
        setShowBanner(true);
        // Trigger animation after state update
        setTimeout(() => setIsVisible(true), 10);
      }, 500);
    }

    // Set up automatic cookie refresh (refreshes every hour)
    const cleanup = setupAutoCookieRefresh();

    // Cleanup on unmount
    return cleanup;
  }, []);

  const handleAcceptAll = () => {
    saveCookiePreference('all');
    setShowBanner(false);
    setIsVisible(false);
    
    // Enable analytics if needed
    if (typeof window !== 'undefined') {
      // Analytics will be enabled by DeferredAnalytics component
      // which checks cookie preferences
    }
  };

  const handleNecessaryOnly = () => {
    saveCookiePreference('necessary');
    setShowBanner(false);
    setIsVisible(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-[9998] transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleNecessaryOnly}
      />

      {/* Cookie Consent Banner */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[9999] transition-transform duration-300 ease-out ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-6 pb-2 sm:pb-4">
          <div className="bg-white dark:bg-dark-800 rounded-md sm:rounded-lg shadow-2xl border border-gray-200 dark:border-dark-700 p-2.5 sm:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
              {/* Icon and Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2 sm:gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <Cookie className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900 dark:text-gray-100 mb-1 sm:mb-1.5">
                      Cookie Preferences
                    </h3>
                    <p className="text-[11px] sm:text-sm text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2 leading-relaxed">
                      We use cookies to enhance your browsing experience and analyze site traffic.
                      <span className="hidden sm:inline"> Location services are enabled in both modes.</span>
                    </p>
                    <div className="hidden sm:flex items-center gap-1.5 text-[10px] sm:text-xs text-gray-500 dark:text-gray-500 mb-2">
                      <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                      <span className="leading-tight">Location services enabled in both modes</span>
                    </div>
                    <div className="hidden sm:flex flex-wrap gap-1.5 text-[10px] sm:text-xs text-gray-500 dark:text-gray-500">
                      <a
                        href="/privacy-policy"
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>
                      <span>•</span>
                      <a
                        href="/cookie-policy"
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Cookie Policy
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-row gap-1.5 sm:gap-2 w-full sm:w-auto flex-shrink-0">
                <button
                  onClick={handleNecessaryOnly}
                  className="px-2.5 py-1.5 sm:px-4 sm:py-2 text-[11px] sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 rounded-md sm:rounded-lg transition-colors whitespace-nowrap"
                >
                  Necessary Only
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-2.5 py-1.5 sm:px-4 sm:py-2 text-[11px] sm:text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-md sm:rounded-lg transition-colors whitespace-nowrap"
                >
                  Allow All
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

